import { useDataEnvironments } from 'api/queries/useDataEnvironments';
import { showError } from 'helpers/showError';
import { toNumber } from 'helpers/toNumber';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DataEnvironment } from '../types/DataEnvironment';

type SetState = (dataEnvironment: DataEnvironment | undefined) => void;
// NOTE: ContextValue was previously DataEnvironment | undefined but within our application we
// know there will be a data environment set/selected. If there isn't that's a problem. We
// remove the undefined b/c it helps simplify usage within the app no ? or ! everywhere.
export type ContextValue = [DataEnvironment, SetState];
type DataEnvironmentProviderProps = { children: React.ReactNode };

const DataEnvironmentContext = createContext<ContextValue | undefined>(
  undefined
);

function DataEnvironmentProvider({ children }: DataEnvironmentProviderProps) {
  // We are not using session storage for the initial value b/c we store `dataEnvironment` but "cache" id (today)
  // We also use dataEnvironment within the application and done this way we can ensure they've been loaded
  const [dataEnvironment, _setDataEnvironment] = useState<
    DataEnvironment | undefined
  >();

  const setDataEnvironment = useCallback(
    (environment: DataEnvironment | undefined) => {
      // Doing this here, rather than within a useEffect watching dataEnvironment b/c on initial
      // render that would be triggered potentially overwriting the session storage value if we weren't
      // careful. This way it just naturally happens as the application updates the data environment.
      localStorage.setItem('dataEnvironmentId', `${environment?.id}`);
      _setDataEnvironment(environment);
    },
    [_setDataEnvironment]
  );

  const previousDataEnvironmentId: number | undefined = useMemo(() => {
    const stored = localStorage.getItem('dataEnvironmentId');
    return toNumber(stored);
  }, []);

  useDataEnvironments({
    onSuccess: (data) => {
      if (data.length === 0) {
        // TODO: Setup a "User setup bad page"
        showError(
          'User is not associated with any data environments. Please check gateway setup.'
        );
        return;
      }

      const current = data.find((env) => env.id === previousDataEnvironmentId);
      setDataEnvironment(current ?? data[0]);
    },
    onError: () => {
      // TODO: Setup an "Error initializing page with a 'retry'?"
      showError('Error occurred loading available data environments.');
    },
  });

  const value = useMemo<ContextValue>(
    () => [dataEnvironment as DataEnvironment, setDataEnvironment],
    [dataEnvironment, setDataEnvironment]
  );

  return (
    <DataEnvironmentContext.Provider value={value}>
      {/* 
        This pattern will prevent the app mounting without a selected data environment 
        would probably be nicer to provide a full page loading/initializing experience.
      */}
      {/* TODO: Setup a loader/initializing experience */}
      {children}
    </DataEnvironmentContext.Provider>
  );
}

function useDataEnvironment() {
  const context = useContext(DataEnvironmentContext);

  if (context === undefined) {
    throw new Error(
      'useDataEnvironment must be used within a DataEnvironmentProvider'
    );
  }

  return context;
}

export { DataEnvironmentProvider, useDataEnvironment, DataEnvironmentContext };
